import React from 'react';
import { Teacher } from '@/admin/config/listConfig';
import { accountService } from '@/admin/services/account';
import { CreateAccountRequest, UpdateAccountRequest } from '@/school-management/types/request';

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);
const minDate = yesterday.toISOString().split('T')[0];

// Helper function to transform form data to API payload
const transformFormDataToCreateAccountRequest = (formData: any): CreateAccountRequest => {
  return {
    accountManager: formData['admins.0.email'] || '',
    name: formData.accountName || '',
    languagePreferences: {
      learningLanguage: formData.learningLanguage?.value || 'english',
      interfaceLanguage: formData.learningLanguage?.value || 'english',
      nativeLanguage: formData.nativeLanguage?.value || 'english',
      fromToLanguageParams: [
        formData.nativeLanguage?.value || 'english',
        formData.learningLanguage?.value || 'english',
      ],
    },
    isTutorActive: formData.tutor || false,
    accountType:
      formData.accountType?.value === 'school' ? 'Customer_Pilot' : 'Customer_Production',
    tutorType:
      formData.displayType === 'voice'
        ? 'VOICE'
        : formData.displayType === 'avatar'
          ? 'AVATAR'
          : 'TEXT',
    approvedDomain: formData.approvedDomain || '',
    systemType: 'default',
    loginProvider: 'Google',
    signInOptions: 'Email',
    userRegistrationLimit: formData.permittedUsers?.value || 100,
    expirationDate:
      formData.expirationDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
  };
};

// Helper function to transform form data to update API payload
export const transformFormDataToUpdateAccountRequest = (formData: any): UpdateAccountRequest => {
  return {
    accountManager: formData['admins.0.email'] || '',
    name: formData.accountName || '',
    languagePreferences: {
      learningLanguage: formData.learningLanguage?.value || 'english',
      interfaceLanguage: formData.learningLanguage?.value || 'english',
      nativeLanguage: formData.nativeLanguage?.value || 'english',
      fromToLanguageParams: [
        formData.nativeLanguage?.value || 'english',
        formData.learningLanguage?.value || 'english',
      ],
    },
    isTutorActive: formData.tutor || false,
    accountType:
      formData.accountType?.value === 'school' ? 'Customer_Pilot' : 'Customer_Production',
    tutorType:
      formData.displayType === 'voice'
        ? 'VOICE'
        : formData.displayType === 'avatar'
          ? 'AVATAR'
          : 'TEXT',
    approvedDomain: formData.approvedDomain || '',
    systemType: 'default',
    loginProvider: 'Google',
    signInOptions: 'Email',
    userRegistrationLimit: formData.permittedUsers?.value || 100,
    expirationDate:
      formData.expirationDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
  };
};

export interface AdminFields {
  email: string;
  firstName: string;
  lastName: string;
}

export interface FieldConfig {
  name: string; // Unique field name
  type:
    | 'text'
    | 'title'
    | 'email'
    | 'number'
    | 'password'
    | 'select'
    | 'multi-select'
    | 'toggle'
    | 'segmented-toggle'
    | 'date'
    | 'textarea'
    | 'dropdown'
    | 'tags'
    | 'button'
    | 'search'
    | 'tab'
    | 'separator'
    | 'ExtraSeparator';
  logo?: string; // Logo for title fields
  MainTitle?: string; // Main title for title fields
  subTitle?: string; // Subtitle for title fields
  label?: string; // Field label
  placeholder?: string; // Placeholder text
  searchPlaceholder?: string; // Placeholder text for search input in multi-select
  defaultValue?: any; // Default value
  options?: { label: string; value: any; picture?: string }[]; // For select/dropdown/segmented-toggle fields
  value?: string; // For segmented-toggle
  loading?: boolean; // Show loading spinner inside the button
  disabled?: boolean;
  block?: boolean; // Full-width button
  secondary?: boolean; // Apply secondary styling
  isTransparent?: boolean; // Transparent style
  picture?: string; // Picture for title fields
  isBack?: boolean; // Back button
  linkTo?: string; // Link to add more
  buttonText?: string; // Button text
  withHeader?: boolean;
  showSelectAll?: boolean; // Show select/deselect all buttons for multi-select
  maxHeight?: string; // Maximum height for multi-select dropdown
  maxDisplayItems?: number; // Maximum number of items to show in header before truncating
  showAllSelected?: boolean; // Whether to show all selected items or use count in header
  maxSelections?: number; // Maximum number of items that can be selected for multi-select fields
  onLoadMore?: () => void; // Callback to load more options for multi-select
  hasMore?: boolean; // Whether there are more options to load for multi-select
  isLoadingMore?: boolean; // Whether more options are currently being loaded for multi-select
  enableAutocomplete?: boolean; // Enable autocomplete search for multi-select
  excludeValues?: string[];
  isLoadingOptions?: boolean;
  organizationId?: string; // Organization ID for autocomplete
  autocompleteType?: 'teachers' | 'students' | 'groups'; // Type of autocomplete
  autocompleteStartOptions?: { label: string; value: any; data?: any }[]; // Initial options when autocomplete is enabled (no search yet)
  validation?: {
    required?: boolean;
    pattern?: RegExp;
    custom?: (value: any, formValues: any) => string | null; // Custom validation
    messages?: Partial<{
      required: string;
      pattern: string;
      minLength: string;
      maxLength: string;
      min: string;
      max: string;
      email: string;
    }>;
  };
  conditionalDisplay?: (formValues: any) => boolean; // Logic to show/hide field
  events?: {
    onChange?: (value: any, formValues: any, addAdminFields?: () => void) => any;
    onFocus?: () => void;
    onBlur?: () => void;
  };
  style?: React.CSSProperties; // Custom style for the field
  gridSpan?: number; // Number of columns the field should span (1 or 2 ot more)
  smGridSpan?: number; // Number of columns the field should span in small screens
  minLength?: number; // Minimum length for text fields
  maxLength?: number; // Maximum length for text fields

  enforceMaxLength?: boolean;
  min?: string | number; // Minimum value for number/date fields
  max?: string | number; // Maximum value for number/date fields
  spacing?: number; // Spacing between fields
  isAdminGroup?: boolean;
  adminIndex?: number;
}

export interface FormConfig {
  fields: FieldConfig[];
  onSubmit: (formValues: any) => void | Promise<void>; // Global submit handler
}

export const createDealFormConfig: FormConfig = {
  onSubmit: async data => {
    try {
      console.log('Form submitted:', data);
      const payload = transformFormDataToCreateAccountRequest(data);
      console.log('Transformed payload:', payload);

      const response = await accountService.createAccountRegistration(payload);
      console.log('Account created successfully:', response.data);

      // Show success notification
      alert(`Account "${response.data.data.accountName}" created successfully!`);

      // Navigate to accounts list or account details
      window.location.href = '/accounts';
    } catch (error: any) {
      console.error('Error creating account:', error);

      // Show user-friendly error message
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to create account';
      alert(`Error: ${errorMessage}`);

      // Re-throw error to prevent form submission
      throw error;
    }
  },
  fields: [
    {
      type: 'title',
      name: 'main-title',
      MainTitle: 'Create Account',
      gridSpan: 8,
    },
    // General Information Title
    {
      type: 'title',
      name: 'general-info-title',
      label: 'General Information',
      gridSpan: 12,
    },
    {
      type: 'text',
      name: 'accountName',
      label: '*Account Name',
      placeholder: 'Account Name',
      validation: {
        required: true,
        pattern: /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      gridSpan: 4,
    },
    {
      type: 'select',
      name: 'accountType',
      label: '*Account Type',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'School', value: 'school' },
        { label: 'District', value: 'district' },
      ],
      gridSpan: 3,
    },
    {
      type: 'textarea',
      name: 'note',
      label: 'Note',
      placeholder: 'Add Note If Needed',
      gridSpan: 5,
    },
    {
      type: 'select',
      name: 'country',
      label: '*Country',
      validation: {
        required: true,
      },
      defaultValue: { label: 'Brazil', value: 'brazil' },
      options: [
        { label: 'Brazil', value: 'brazil', picture: 'portuguese' },
        { label: 'USA', value: 'usa', picture: 'portuguese' },
        { label: 'Israel', value: 'israel', picture: 'portuguese' },
      ],
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'state',
      label: '*State',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      defaultValue: { label: 'Minas Gerais', value: 'minas-gerais' },
      options: [
        { label: 'Minas Gerais', value: 'minas-gerais' },
        { label: 'São Paulo', value: 'sao-paulo' },
        { label: 'Rio de Janeiro', value: 'rio-de-janeiro' },
      ],
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'nativeLanguage',
      label: '*Native Language',
      validation: {
        required: true,
      },
      defaultValue: { label: 'Portuguese', value: 'portuguese' },
      options: [
        { label: 'Portuguese', value: 'portuguese', picture: 'portuguese' },
        { label: 'English', value: 'english', picture: 'portuguese' },
        { label: 'Hebrew', value: 'hebrew', picture: 'portuguese' },
      ],
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'learningLanguage',
      label: '*Learning Language',
      validation: {
        required: true,
      },
      defaultValue: { label: 'English', value: 'english' },
      options: [{ label: 'English', value: 'english' }],
      gridSpan: 3,
    },
    {
      type: 'separator',
      name: 'separator-2',
      gridSpan: 12,
    },
    // Deal Admin Title
    {
      type: 'title',
      name: 'deal-admin-title',
      label: 'Account Admin',
      subTitle: 'Super-admins manage all accounts. Add an account admin for this account only.',
      buttonText: '+ Add More',
      linkTo: '#',
      gridSpan: 12,
      events: {
        onChange: (_, formValues, addAdminFields) => {
          if (addAdminFields) {
            addAdminFields();
          }
        },
      },
    },
    {
      type: 'email',
      name: 'admins.0.email',
      label: 'Email',
      placeholder: 'Email',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      },
      gridSpan: 6,
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'text',
      name: 'admins.0.firstName',
      label: 'First Name',
      placeholder: 'First Name',
      gridSpan: 3,
      maxLength: 30,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'text',
      name: 'admins.0.lastName',
      label: 'Last Name',
      placeholder: 'Last Name',
      gridSpan: 3,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'separator',
      name: 'separator-3-create',
      gridSpan: 12,
    },
    // State Admin Title
    {
      type: 'title',
      name: 'state-admin-title',
      label: 'State Admin',
      subTitle: 'Super-admins manage the State. Add a State admin for this State only.',
      gridSpan: 12,
    },
    {
      type: 'email',
      name: 'stateAdmins.0.email',
      label: 'Email',
      placeholder: 'Email',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      },
      gridSpan: 6,
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'text',
      name: 'stateAdmins.0.firstName',
      label: 'First Name',
      placeholder: 'First Name',
      gridSpan: 3,
      maxLength: 30,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'text',
      name: 'stateAdmins.0.lastName',
      label: 'Last Name',
      placeholder: 'Last Name',
      gridSpan: 3,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'separator',
      name: 'separator-4-create',
      gridSpan: 12,
    },
    // Settings Title
    {
      type: 'title',
      name: 'settings-title',
      label: 'Limitations',
      gridSpan: 12,
    },
    {
      type: 'date',
      name: 'startDate',
      label: 'Start Date',
      min: minDate, // Cannot pick before yesterday
      max: '2030-12-31',
      gridSpan: 3,
    },
    {
      type: 'date',
      name: 'expirationDate',
      label: 'Expiration Date',
      min: minDate, // Cannot pick before yesterday
      max: '2030-12-31',
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'permittedUsers',
      label: '*Permitted User',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: '100', value: 100 },
        { label: '500', value: 500 },
        { label: '1000', value: 1000 },
        { label: '5000', value: 5000 },
      ],
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'lms',
      label: '*LMS',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [{ label: 'Email', value: 'email' }],
      gridSpan: 3,
    },
    {
      type: 'toggle',
      name: 'regions',
      label: 'Regions',
      subTitle: 'if you want to add more than 1 school, you have to create region first',
      gridSpan: 12,
    },
    {
      type: 'separator',
      name: 'separator-4-edit',
      gridSpan: 12,
    },
    // Tutor toggle

    {
      type: 'toggle',
      name: 'tutor',
      label: 'Tutor - An AI-Powered Feature That Acts As A Private English Tutor For Students.',
      gridSpan: 12,
    },
    {
      type: 'segmented-toggle',
      name: 'displayType',
      label: 'Display',
      options: [
        { label: 'Voice', value: 'voice' },
        { label: 'Avatar', value: 'avatar' },
      ],
      defaultValue: 'avatar',
      gridSpan: 12,
    },
    {
      type: 'segmented-toggle',
      name: 'viewType',
      label: 'View',
      options: [
        { label: 'Video', value: 'Video' },
        { label: 'Animation', value: 'Animation' },
      ],
      defaultValue: 'Animation',
      gridSpan: 12,
    },
    {
      type: 'ExtraSeparator',
      name: 'ExtraSeparator-2',
      spacing: 40,
      gridSpan: 12,
    },
    // Buttons
    {
      type: 'button',
      name: 'Save',
      label: 'Save',
      events: {
        onChange: (allValues: Record<string, any>) => {
          console.log('Clicked Save', allValues);
        },
      },
      gridSpan: 6,
    },
    {
      type: 'button',
      name: 'submit',
      label: 'Save & Create Region',
      block: false,
      gridSpan: 6,
      events: {
        onChange: (formValues: Record<string, any>) => {
          console.log('📝 Save & Create Region clicked! Here is a summary of your form:');
          Object.entries(formValues || {}).forEach(([key, value]) => {
            console.log(`- ${key}:`, value);
          });
        },
      },
    },
  ],
};

export const addDistrictFormConfig: FormConfig = {
  onSubmit: data => {
    console.log('Add District form submitted:', data);
  },
  fields: [
    {
      type: 'title',
      name: 'main-title',
      MainTitle: 'New Region',
      gridSpan: 12,
    },
    {
      type: 'title',
      name: 'account-info-title',
      label: 'Account Information',
      gridSpan: 12,
    },
    {
      type: 'select',
      name: 'deal',
      label: '*Account',
      placeholder: 'Choose Account',
      validation: {
        required: true,
      },
      options: [
        { label: 'Deal 1', value: 'deal1' },
        { label: 'Deal 2', value: 'deal2' },
      ],
      gridSpan: 12,
    },
    {
      type: 'title',
      name: 'general-info-title',
      label: 'General Information',
      gridSpan: 12,
    },
    {
      type: 'text',
      name: 'regionName',
      label: '*Region Name',
      placeholder: 'Region Name',
      validation: {
        required: true,
      },
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'country',
      label: '*Country',
      validation: {
        required: true,
      },
      defaultValue: { label: 'Brazil', value: 'brazil' },
      options: [
        { label: 'Brazil', value: 'brazil', picture: 'portuguese' },
        { label: 'USA', value: 'usa', picture: 'portuguese' },
        { label: 'Israel', value: 'israel', picture: 'portuguese' },
      ],
      gridSpan: 3,
    },
    {
      type: 'text',
      name: 'state',
      label: '*State',
      placeholder: 'State',
      validation: {
        required: true,
      },
      gridSpan: 3,
    },
    {
      type: 'text',
      name: 'city',
      label: '*City',
      placeholder: 'City',
      validation: {
        required: true,
      },
      gridSpan: 3,
    },
    {
      type: 'title',
      name: 'region-manager-title',
      label: 'Region Manager',
      subTitle: 'Region Manager will be sent credentials via email automatically.',
      gridSpan: 12,
    },
    {
      type: 'email',
      name: 'managerEmail',
      label: '*Email',
      placeholder: 'Email',
      validation: {
        required: true,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      },
      gridSpan: 6,
    },
    {
      type: 'text',
      name: 'managerFirstName',
      label: '*First Name',
      placeholder: 'First Name',
      validation: {
        required: true,
      },
      gridSpan: 3,
    },
    {
      type: 'text',
      name: 'managerLastName',
      label: '*Last Name',
      placeholder: 'Last Name',
      validation: {
        required: true,
      },
      gridSpan: 3,
    },
    {
      type: 'ExtraSeparator',
      name: 'ExtraSeparator-1',
      spacing: 40,
      gridSpan: 12,
    },
    {
      type: 'button',
      name: 'save',
      label: 'Save & Done',
      secondary: true,
      gridSpan: 9,
    },
    {
      type: 'button',
      name: 'submit',
      label: 'Save & Create Another Region',
      style: {
        width: '316px',
      },
      block: false,
      gridSpan: 3,
      events: {
        onChange: values => {
          console.log('Save & Create Another Region clicked', values);
        },
      },
    },
  ],
};

export const editDealFormConfig: FormConfig = {
  onSubmit: async data => {
    // This will be overridden by the EditAccountFormPage component
    console.log('Edit Account form submitted:', data);
  },
  fields: [
    {
      type: 'title',
      name: 'main-title',
      MainTitle: 'Edit Account',
      style: { padding: '0px' },
    },
    {
      type: 'title',
      name: 'general-info-title',
      label: 'General Information',
      gridSpan: 12,
    },
    {
      type: 'text',
      name: 'accountName',
      label: '*Account Name',
      placeholder: 'Account Name',
      validation: {
        required: true,
        pattern: /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      gridSpan: 4,
    },
    {
      type: 'select',
      name: 'accountType',
      label: '*Account Type',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'School', value: 'school' },
        { label: 'District', value: 'district' },
      ],
      gridSpan: 3,
    },
    {
      type: 'textarea',
      name: 'note',
      label: 'Note',
      placeholder: 'Add Note If Needed',
      gridSpan: 5,
    },
    {
      type: 'select',
      name: 'country',
      label: '*Country',
      validation: {
        required: true,
      },
      defaultValue: { label: 'Brazil', value: 'brazil' },
      options: [
        { label: 'Brazil', value: 'brazil', picture: 'portuguese' },
        { label: 'USA', value: 'usa', picture: 'portuguese' },
        { label: 'Israel', value: 'israel', picture: 'portuguese' },
      ],
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'state',
      label: '*State',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      defaultValue: { label: 'Minas Gerais', value: 'minas-gerais' },
      options: [
        { label: 'Minas Gerais', value: 'minas-gerais' },
        { label: 'São Paulo', value: 'sao-paulo' },
        { label: 'Rio de Janeiro', value: 'rio-de-janeiro' },
      ],
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'nativeLanguage',
      label: '*Native Language',
      validation: {
        required: true,
      },
      defaultValue: { label: 'Portuguese', value: 'portuguese' },
      options: [
        { label: 'Portuguese', value: 'portuguese', picture: 'portuguese' },
        { label: 'English', value: 'english', picture: 'portuguese' },
        { label: 'Hebrew', value: 'hebrew', picture: 'portuguese' },
      ],
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'learningLanguage',
      label: '*Learning Language',
      validation: {
        required: true,
      },
      defaultValue: { label: 'English', value: 'english' },
      options: [{ label: 'English', value: 'english' }],
      gridSpan: 3,
    },
    {
      type: 'separator',
      name: 'separator-2',
      gridSpan: 12,
    },
    {
      type: 'title',
      name: 'deal-admin-title',
      label: 'Account Admin',
      subTitle: 'Super-admins manage all accounts. Add an account admin for this account only.',
      buttonText: '+ Add More',
      linkTo: '#',
      gridSpan: 12,
      events: {
        onChange: (_, formValues, addAdminFields) => {
          if (addAdminFields) {
            addAdminFields();
          }
        },
      },
    },
    {
      type: 'email',
      name: 'admins.0.email',
      label: 'Email',
      placeholder: 'Email',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      },
      gridSpan: 6,
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'text',
      name: 'admins.0.firstName',
      label: 'First Name',
      placeholder: 'First Name',
      gridSpan: 3,
      maxLength: 30,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'text',
      name: 'admins.0.lastName',
      label: 'Last Name',
      placeholder: 'Last Name',
      gridSpan: 3,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'separator',
      name: 'separator-3-edit',
      gridSpan: 12,
    },
    // State Admin Title
    {
      type: 'title',
      name: 'state-admin-title',
      label: 'State Admin',
      subTitle: 'Super-admins manage the State. Add a State admin for this State only.',
      gridSpan: 12,
    },
    {
      type: 'email',
      name: 'stateAdmins.0.email',
      label: 'Email',
      placeholder: 'Email',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      },
      gridSpan: 6,
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'text',
      name: 'stateAdmins.0.firstName',
      label: 'First Name',
      placeholder: 'First Name',
      gridSpan: 3,
      maxLength: 30,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'text',
      name: 'stateAdmins.0.lastName',
      label: 'Last Name',
      placeholder: 'Last Name',
      gridSpan: 3,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      isAdminGroup: true,
      adminIndex: 0,
    },
    {
      type: 'separator',
      name: 'separator-4-edit-form',
      gridSpan: 12,
    },
    {
      type: 'title',
      name: 'settings-title',
      label: 'Limitations',
      gridSpan: 12,
    },
    {
      type: 'date',
      name: 'startDate',
      label: 'Start Date',
      min: minDate,
      max: '2030-12-31',
      gridSpan: 3,
    },
    {
      type: 'date',
      name: 'expirationDate',
      label: 'Expiration Date',
      min: minDate,
      max: '2030-12-31',
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'permittedUsers',
      label: '*Permitted User',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: '100', value: 100 },
        { label: '500', value: 500 },
        { label: '1000', value: 1000 },
        { label: '5000', value: 5000 },
      ],
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'lms',
      label: '*LMS',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [{ label: 'Email', value: 'email' }],
      gridSpan: 3,
    },
    {
      type: 'toggle',
      name: 'regions',
      label: 'Regions',
      subTitle: 'if you want to add more than 1 school, you have to create region first',
      gridSpan: 12,
    },
    {
      type: 'toggle',
      name: 'tutor',
      label: 'Tutor - An AI-Powered Feature That Acts As A Private English Tutor For Students.',
      defaultValue: true,
      gridSpan: 12,
    },
    {
      type: 'segmented-toggle',
      name: 'displayType',
      label: 'Display',
      options: [
        { label: 'Voice', value: 'voice' },
        { label: 'Avatar', value: 'avatar' },
      ],
      defaultValue: 'avatar',
      gridSpan: 12,
    },
    {
      type: 'segmented-toggle',
      name: 'viewType',
      label: 'View',
      options: [
        { label: 'Video', value: 'Video' },
        { label: 'Animation', value: 'Animation' },
      ],
      defaultValue: 'Animation',
      gridSpan: 12,
    },
    {
      type: 'ExtraSeparator',
      name: 'ExtraSeparator-2',
      spacing: 40,
      gridSpan: 12,
    },
    {
      type: 'button',
      name: 'cancel',
      label: 'Cancel',
      events: {
        onChange: () => {
          window.history.back();
        },
      },
      gridSpan: 6,
    },
    {
      type: 'button',
      name: 'submit',
      label: 'Save',
      style: {
        width: 200,
      },
      gridSpan: 6,
    },
  ],
};

export const addSchoolFormConfig: FormConfig = {
  onSubmit: data => {
    console.log('Add School form submitted:', data);
  },
  fields: [
    {
      type: 'title',
      name: 'main-title',
      MainTitle: 'New School',
      gridSpan: 12,
    },
    // General Information Section
    {
      type: 'title',
      name: 'general-info-title',
      label: 'General Information',
      gridSpan: 12,
    },
    {
      type: 'text',
      name: 'schoolName',
      label: '*School Name',
      placeholder: 'School Name',
      validation: {
        required: true,
        pattern: /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
      gridSpan: 4,
    },
    {
      type: 'select',
      name: 'schoolType',
      label: '*School Type',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'Public', value: 'public' },
        { label: 'Private', value: 'private' },
        { label: 'Charter', value: 'charter' },
      ],
      gridSpan: 4,
    },
    {
      type: 'select',
      name: 'city',
      label: '*City',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'São Paulo', value: 'sao-paulo' },
        { label: 'Rio de Janeiro', value: 'rio' },
        { label: 'Brasília', value: 'brasilia' },
      ],
      gridSpan: 4,
    },
    {
      type: 'separator',
      name: 'sep-district-info',
      gridSpan: 12,
    },
    // District Information Section
    {
      type: 'title',
      name: 'account-region-title',
      label: 'Account & Region Information',
      gridSpan: 12,
    },
    {
      type: 'select',
      name: 'account',
      label: '*Account',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'Account 1', value: 'account1' },
        { label: 'Account 2', value: 'account2' },
      ],
      gridSpan: 4,
    },
    {
      type: 'select',
      name: 'country',
      label: '*Country',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'Brazil', value: 'brazil', picture: 'portuguese' },
        { label: 'USA', value: 'usa', picture: 'portuguese' },
        { label: 'Israel', value: 'israel', picture: 'portuguese' },
      ],
      gridSpan: 4,
    },
    {
      type: 'select',
      name: 'region',
      label: '*Region',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'Region 1', value: 'region1' },
        { label: 'Region 2', value: 'region2' },
      ],
      gridSpan: 4,
    },
    {
      type: 'separator',
      name: 'sep-district-info',
      gridSpan: 12,
    },
    // Location Section
    {
      type: 'title',
      name: 'location-title',
      label: 'Location',
      gridSpan: 12,
    },
    {
      type: 'select',
      name: 'locationCity',
      label: '*City',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'São Paulo', value: 'sao-paulo' },
        { label: 'Rio de Janeiro', value: 'rio' },
        { label: 'Brasília', value: 'brasilia' },
      ],
      gridSpan: 6,
    },
    {
      type: 'select',
      name: 'locationRegion',
      label: '*Region',
      placeholder: 'Choose',
      validation: {
        required: true,
      },
      options: [
        { label: 'Region 1', value: 'region1' },
        { label: 'Region 2', value: 'region2' },
      ],
      gridSpan: 6,
    },
    // School Manager Section
    {
      type: 'title',
      name: 'school-manager-title',
      label: 'School Manager',
      gridSpan: 12,
    },
    {
      type: 'title',
      name: 'school-manager-helper',
      subTitle: 'Lorem ipsum dolor sit amet consectetur.',
      gridSpan: 12,
    },
    {
      type: 'email',
      name: 'email',
      label: 'Email',
      placeholder: 'Email',
      gridSpan: 6,
    },
    {
      type: 'text',
      name: 'firstName',
      label: 'First Name',
      placeholder: 'First Name',
      gridSpan: 3,
      maxLength: 30,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
    },
    {
      type: 'text',
      name: 'lastName',
      label: 'Last Name',
      placeholder: 'Last Name',
      gridSpan: 3,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        pattern: /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
    },
    {
      type: 'ExtraSeparator',
      name: 'ExtraSeparator-2',
      spacing: 40,
      gridSpan: 12,
    },
    // Buttons
    {
      type: 'button',
      name: 'cancel',
      label: 'Cancel',
      secondary: true,
      gridSpan: 11,
      events: {
        onChange: () => {
          window.history.back();
        },
      },
    },
    {
      type: 'button',
      name: 'save',
      label: 'Save',
      style: {
        width: '135px',
      },
      gridSpan: 1,
    },
  ],
};

export const editDistrictFormConfig: FormConfig = {
  onSubmit: data => {
    console.log('Edit District form submitted:', data);
  },
  fields: [
    {
      type: 'title',
      name: 'main-title',
      MainTitle: 'Region Name',
    },
    // Account Information Section
    {
      type: 'title',
      name: 'account-info-title',
      label: 'Account Information',
      gridSpan: 12,
    },
    {
      type: 'select',
      name: 'account',
      label: '*Account',
      placeholder: 'Choose Account',
      options: [{ label: 'ACCOUNT1', value: 'account1' }],
      gridSpan: 12,
    },
    // General Information Section
    {
      type: 'title',
      name: 'general-info-title',
      label: 'General Information',
      gridSpan: 12,
    },
    {
      type: 'text',
      name: 'regionName',
      label: '*Region Name',
      placeholder: 'Region1',
      validation: {
        required: true,
      },
      gridSpan: 3,
    },
    {
      type: 'select',
      name: 'country',
      label: '*Country',
      validation: {
        required: true,
      },
      defaultValue: { label: 'Brazil', value: 'brazil' },
      options: [{ label: 'Brazil', value: 'brazil', picture: 'portuguese' }],
      gridSpan: 3,
    },
    {
      type: 'text',
      name: 'state',
      label: '*State',
      placeholder: 'Lorem',
      validation: {
        required: true,
      },
      gridSpan: 3,
    },
    {
      type: 'text',
      name: 'city',
      label: '*City',
      placeholder: 'Ipsum',
      validation: {
        required: true,
      },
      gridSpan: 3,
    },
    // Region Manager Section
    {
      type: 'title',
      name: 'region-manager-title',
      label: 'Region Manager',
      subTitle: 'Lorem ipsum dolor sit amet consectetur.',
      gridSpan: 12,
    },
    {
      type: 'email',
      name: 'email',
      label: 'Email',
      placeholder: 'henrys@melingo.com',
      validation: {
        required: true,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      },
      gridSpan: 12,
    },
    {
      type: 'text',
      name: 'firstName',
      label: 'First Name',
      placeholder: 'Henry',
      validation: {
        required: true,
      },
      gridSpan: 6,
    },
    {
      type: 'text',
      name: 'lastName',
      label: 'Last Name',
      placeholder: 'Spierer',
      validation: {
        required: true,
      },
      gridSpan: 6,
    },
    {
      type: 'ExtraSeparator',
      name: 'ExtraSeparator-1',
      spacing: 10,
      gridSpan: 12,
    },
    {
      type: 'button',
      name: 'cancel',
      label: 'Cancel',
      events: {
        onChange: () => {
          window.history.back();
        },
      },
      gridSpan: 6,
    },
    {
      type: 'button',
      name: 'submit',
      label: 'Save',
      style: {
        width: '135px',
      },
      gridSpan: 6,
    },
  ],
};

export type TranslateFn = (key: string) => string;

export const getEditTeacherFormConfig = (t: TranslateFn): FormConfig => ({
  onSubmit: data => {
    console.log('Edit Teacher form submitted:', data);
  },
  fields: [
    {
      type: 'title',
      name: 'main-title',
      MainTitle: t('form.editTeacher'),
      gridSpan: 12,
    },
    {
      type: 'text',
      name: 'firstName',
      label: t('form.firstName'),
      placeholder: t('form.firstName'),
      gridSpan: 3,
      smGridSpan: 6,
      maxLength: 30,
      enforceMaxLength: false,
      validation: {
        required: true,
        pattern: /^[-A-Za-z\s'"\u2013.,\u0590-\u05FF\u00C0-\u00FF\u0100-\u01FF\u0200-\u02AF]+$/,
        messages: {
          required: 'settings.add_teachers.errors.first_name_missing',
          pattern: 'settings.add_teachers.errors.first_name_incorrect',
          maxLength: 'settings.add_teachers.errors.first_name_too_long',
        },
      },
      style: { minWidth: 0 },
    },
    {
      type: 'text',
      name: 'lastName',
      label: t('form.lastName'),
      placeholder: t('form.lastName'),
      gridSpan: 4,
      smGridSpan: 6,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        required: true,
        pattern: /^[-A-Za-z\s'"\u2013.,\u0590-\u05FF\u00C0-\u00FF\u0100-\u01FF\u0200-\u02AF]+$/,
        messages: {
          required: 'settings.add_teachers.errors.last_name_missing',
          pattern: 'settings.add_teachers.errors.last_name_incorrect',
          maxLength: 'settings.add_teachers.errors.last_name_too_long',
        },
      },
      style: { minWidth: 0 },
    },
    {
      type: 'email',
      name: 'email',
      label: t('form.email'),
      placeholder: t('form.email'),
      gridSpan: 5,
      smGridSpan: 12,
      validation: {
        required: true,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      },
      style: { minWidth: 0, opacity: 0.5 },
      disabled: true,
    },
    {
      type: 'button',
      name: 'cancel',
      label: t('common.cancel'),
      secondary: true,
      style: {
        justifyContent: 'flex-start',
        marginTop: '22px',
        color: '#145c99',
        fontSize: '18px',
        fontWeight: '600',
      },
      gridSpan: 11,
    },
    {
      type: 'button',
      name: 'save',
      label: t('common.save'),
      style: {
        width: 86,
        justifyContent: 'space-evenly',
        alignItems: 'center',
        background: '#B5B5B5',
        marginTop: '22px',
        fontWeight: '600',
      },
      gridSpan: 1,
    },
  ],
});

export const createClassFormConfig = (teachers: Teacher[]): FormConfig => ({
  onSubmit: () => {},
  fields: [
    {
      type: 'title' as const,
      name: 'new-class-title',
      MainTitle: 'New Class',
      gridSpan: 12,
    },
    {
      type: 'text' as const,
      name: 'className',
      label: 'Class Name',
      placeholder: 'Write here',
      gridSpan: 4,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        required: true,
        pattern: /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
    },
    {
      type: 'select' as const,
      name: 'grade',
      label: 'Grade',
      placeholder: 'Choose',
      options: [
        { label: '1st', value: '1st' },
        { label: '2nd', value: '2nd' },
        { label: '3rd', value: '3rd' },
      ],
      gridSpan: 4,
      validation: { required: true },
    },
    {
      type: 'select' as const,
      name: 'teacher',
      label: 'Teacher',
      placeholder: 'Choose',
      options: teachers.map((t: Teacher) => ({
        label: `${t.firstName} ${t.lastName}`,
        value: t.id,
      })),

      gridSpan: 4,
      validation: { required: true },
    },
  ] as FieldConfig[],
});
